import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { LogoIcon, LogoText } from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';

// Variant A: Premium & Sleek
// Dark, minimal, luxury SaaS feel with subtle animations and glass effects

function LandingA() {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const previewRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.fade-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const { t, i18n } = useTranslation(['landing', 'common']);

  return (
    <div className="min-h-screen bg-anthracite-950 overflow-hidden">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('landing:metaTitle')}</title>
        <meta name="description" content={t('landing:metaDesc')} />
        <link rel="canonical" href="https://scorinova.com/" />
      </Helmet>
      <style>{`
        .fade-up {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .fade-up.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .fade-up-delay-1 { transition-delay: 0.1s; }
        .fade-up-delay-2 { transition-delay: 0.2s; }
        .fade-up-delay-3 { transition-delay: 0.3s; }
        .fade-up-delay-4 { transition-delay: 0.4s; }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes line-draw {
          from { stroke-dashoffset: 1000; }
          to { stroke-dashoffset: 0; }
        }
        .float-slow { animation: float 6s ease-in-out infinite; }
        .float-medium { animation: float 4s ease-in-out infinite; animation-delay: -2s; }
        .float-fast { animation: float 3s ease-in-out infinite; animation-delay: -1s; }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .gradient-text {
          background: linear-gradient(135deg, #ff6b2c, #ff8c5a, #ff6b2c);
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass {
          background: rgba(18, 18, 20, 0.6);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .glass-card {
          background: rgba(18, 18, 20, 0.4);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .hero-grid {
          background-image:
            linear-gradient(rgba(255, 107, 44, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 107, 44, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }
        .orb {
          filter: blur(80px);
          will-change: transform;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <LogoIcon className="w-9 h-9" />
            <LogoText size="text-lg" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
              {t('common:signIn')}
            </Link>
            <Link to="/signup" className="px-5 py-2 text-sm font-medium bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors">
              {t('common:getStarted')}
            </Link>
            <LanguageSelector compact />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center hero-grid">
        {/* Ambient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-accent/8 orb float-slow" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-accent/5 orb float-medium" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full bg-orange-400/5 orb float-fast" />

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 pt-24">
          {/* Badge */}
          <div className="fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-8">
            <div className="w-2 h-2 rounded-full bg-green-500 pulse-glow" />
            <span className="text-xs text-gray-400 font-medium">{t('landing:badgeText')}</span>
          </div>

          {/* Headline */}
          <h1 className="fade-up fade-up-delay-1 text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6">
            {t('landing:heroHeadline1')}<br />
            <span className="gradient-text">{t('landing:heroHeadline2')}</span>
          </h1>

          {/* Subtitle */}
          <p className="fade-up fade-up-delay-2 text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('landing:heroSubtitle')}
          </p>

          {/* CTA */}
          <div className="fade-up fade-up-delay-3 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="group px-8 py-4 bg-accent hover:bg-accent-dark text-white font-semibold rounded-xl transition-all shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] flex items-center gap-2"
            >
              {t('landing:startForFree')}
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <a href="#features" className="px-8 py-4 text-gray-400 hover:text-white font-medium transition-colors">
              {t('landing:seeHowItWorks')}
            </a>
          </div>

          {/* Hero visual — Floating prediction cards */}
          <div className="fade-up fade-up-delay-4 relative mt-20 mb-8">
            <div className="relative max-w-3xl mx-auto">
              {/* Main card */}
              <div className="glass rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-accent" />
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">{t('landing:todaysAIPicks')}</span>
                  </div>
                  <span className="text-xs text-gray-600">Feb 10, 2026</span>
                </div>

                {/* Mock prediction rows */}
                <div className="space-y-3">
                  {[
                    { home: 'Bayern Munich', away: 'Dortmund', league: 'Bundesliga', pick: 'BTTS Yes', grade: 'A+', gradeColor: 'text-emerald-400 bg-emerald-500/20' },
                    { home: 'Arsenal', away: 'Liverpool', league: 'Premier League', pick: 'Over 2.5', grade: 'A', gradeColor: 'text-green-400 bg-green-500/20' },
                    { home: 'Barcelona', away: 'Real Madrid', league: 'La Liga', pick: 'Home Win', grade: 'A+', gradeColor: 'text-emerald-400 bg-emerald-500/20' },
                  ].map((match, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-anthracite-800/50 border border-anthracite-700/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium">{match.home} vs {match.away}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{match.league}</div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <span className="text-xs font-semibold text-accent bg-accent/10 px-2.5 py-1 rounded-lg">{match.pick}</span>
                        <span className={`text-xs font-bold px-2 py-1 rounded-lg ${match.gradeColor}`}>{match.grade}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Blur overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-anthracite-950/90 to-transparent rounded-b-2xl" />
              </div>

              {/* Floating side cards */}
              <div className="hidden md:block absolute -left-16 top-8 glass-card rounded-xl p-4 w-44 float-slow">
                <div className="text-xs text-gray-500 mb-1">{t('landing:winRateLabel')}</div>
                <div className="text-2xl font-bold text-white">68.4%</div>
                <div className="text-xs text-green-400 mt-1">{t('landing:thisMonth')}</div>
              </div>

              <div className="hidden md:block absolute -right-16 top-20 glass-card rounded-xl p-4 w-44 float-medium">
                <div className="text-xs text-gray-500 mb-1">{t('landing:markets')}</div>
                <div className="text-2xl font-bold text-white">7</div>
                <div className="text-xs text-gray-400 mt-1">{t('landing:predictionTypes')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="fade-up text-center mb-20">
            <span className="text-xs font-semibold text-accent uppercase tracking-widest">{t('landing:howItWorks')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4">
              {t('landing:dataInPredictionsOut')}
            </h2>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto">
              {t('landing:howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                ),
                title: t('landing:featureDataTitle'),
                desc: t('landing:featureDataDesc'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                  </svg>
                ),
                title: t('landing:featureAITitle'),
                desc: t('landing:featureAIDesc'),
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
                  </svg>
                ),
                title: t('landing:featureTrackTitle'),
                desc: t('landing:featureTrackDesc'),
              },
            ].map((feature, i) => (
              <div key={i} className={`fade-up fade-up-delay-${i + 1} glass-card rounded-2xl p-8 hover:border-accent/20 transition-all group`}>
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-5 group-hover:bg-accent/20 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets Section */}
      <section ref={previewRef} className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="fade-up text-center mb-16">
            <span className="text-xs font-semibold text-accent uppercase tracking-widest">{t('landing:coverage')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4">
              {t('landing:sevenMarketsOneDashboard')}
            </h2>
          </div>

          <div className="fade-up grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: t('landing:marketOutcome'), desc: t('landing:marketOutcomeDesc'), icon: '1X2' },
              { name: t('landing:marketBTTS'), desc: t('landing:marketBTTSDesc'), icon: 'BTTS' },
              { name: t('landing:marketOver25'), desc: t('landing:marketOver25Desc'), icon: 'O2.5' },
              { name: t('landing:marketDraw'), desc: t('landing:marketDrawDesc'), icon: 'DRW' },
              { name: t('landing:marketUltimate'), desc: t('landing:marketUltimateDesc'), icon: 'ULT' },
              { name: t('landing:marketAI'), desc: t('landing:marketAIDesc'), icon: 'AI' },
              { name: t('landing:market20Leagues'), desc: t('landing:market20LeaguesDesc'), icon: 'EU' },
            ].map((market, i) => (
              <div key={i} className="glass-card rounded-xl p-5 text-center hover:border-accent/20 transition-all group">
                <div className="text-accent font-mono font-bold text-sm mb-2 opacity-60 group-hover:opacity-100 transition-opacity">{market.icon}</div>
                <div className="text-white font-medium text-sm">{market.name}</div>
                <div className="text-gray-500 text-xs mt-1">{market.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="relative py-32 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.03] to-transparent" />
        <div className="fade-up relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t('landing:readyToSee')}
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            {t('landing:createFreeAccount')}
          </p>
          <Link
            to="/signup"
            className="group inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-dark text-white font-semibold rounded-xl transition-all shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02]"
          >
            {t('landing:getStartedFree')}
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-anthracite-800 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LogoText size="text-sm" />
            <span className="text-xs text-gray-600">{t('common:aiFootballPredictions')}</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <Link to="/terms" className="hover:text-accent transition-colors">{t('common:terms')}</Link>
            <Link to="/privacy" className="hover:text-accent transition-colors">{t('common:privacyPolicy')}</Link>
            <Link to="/login" className="hover:text-accent transition-colors">{t('common:signIn')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingA;
